import type { ReactNode } from 'react';
import { Badge, Card } from '../../components/ui';
import type { ComedyChannel } from '../../lib/api';

export const COMEDY_TABS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'channels', label: 'Kanały tematyczne' },
  { id: 'research', label: 'Research AI' },
  { id: 'sources', label: 'Źródła materiałów' },
  { id: 'library', label: 'Biblioteka wypowiedzi' },
  { id: 'scenarios', label: 'Scenariusze AI' },
  { id: 'director', label: 'Comedy Director' },
  { id: 'studio', label: 'Video Studio' },
  { id: 'queue', label: 'Kolejka produkcji' },
  { id: 'schedule', label: 'Harmonogram' },
  { id: 'publications', label: 'Publikacje' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'settings', label: 'Ustawienia' },
  { id: 'help', label: 'Pomoc' },
] as const;
export type ComedyTabId = (typeof COMEDY_TABS)[number]['id'];

export const COMEDY_PLATFORM_LABEL: Record<string, string> = {
  YOUTUBE: 'YouTube', FACEBOOK: 'Facebook', INSTAGRAM: 'Instagram', TIKTOK: 'TikTok',
};

export function toneForStatus(status: string): 'neutral' | 'info' | 'warning' | 'success' | 'danger' {
  const value = status.toUpperCase();
  if (['SENT', 'PUBLISHED', 'READY', 'APPROVED', 'COMPLETED', 'ACTIVE', 'CLEARED', 'CONNECTED', 'PASS', 'OK'].includes(value)) return 'success';
  if (['FAILED', 'REJECTED', 'ERROR', 'RESTRICTED', 'DISCONNECTED', 'MISSING_AUTHORIZATION', 'FAIL', 'DEAD_LETTER'].includes(value)) return 'danger';
  if (['RUNNING', 'RENDERING', 'QUEUED', 'SCHEDULED', 'REVIEW', 'QA_PENDING', 'PENDING', 'WARN', 'DRAFT'].includes(value)) return 'warning';
  if (['METADATA_ONLY', 'UNVERIFIED', 'UNKNOWN', 'NOT_REQUESTED'].includes(value)) return 'neutral';
  return 'info';
}

export function ComedyStatus({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="muted">—</span>;
  return <Badge tone={toneForStatus(value)}>{value}</Badge>;
}

export function formatDate(value: string | null | undefined, withTime = true): string {
  if (!value) return '—';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  return new Intl.DateTimeFormat('pl-PL', {
    timeZone: 'Europe/Warsaw', dateStyle: 'medium', ...(withTime ? { timeStyle: 'short' } : {}),
  }).format(date);
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  const total = Math.max(0, Math.round(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

export function formatMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '—';
  const total = Math.max(0, Math.round(ms / 100) / 10);
  return `${total.toFixed(1)}s`;
}

export function formatUsd(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `$${value.toFixed(3)}`;
}

export function splitList(value: string): string[] {
  return [...new Set(value.split(',').map((item) => item.trim()).filter(Boolean))];
}

export function ComedyStat({ label, value, hint, tone }: { label: string; value: ReactNode; hint?: string; tone?: 'danger' | 'warning' | 'success' }) {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 750, marginTop: 6, color: tone === 'danger' ? 'var(--danger)' : tone === 'warning' ? 'var(--warning)' : tone === 'success' ? 'var(--success)' : undefined }}>{value}</div>
      {hint && <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

export function ComedyEmpty({ title, hint, action }: { title: string; hint: string; action?: ReactNode }) {
  return (
    <div className="empty">
      <div className="empty__icon">🎭</div>
      <div style={{ fontWeight: 600, color: 'var(--text)' }}>{title}</div>
      <div style={{ marginTop: 4 }}>{hint}</div>
      {action && <div style={{ marginTop: 12 }}>{action}</div>}
    </div>
  );
}

export function ChannelBadge({ channel }: { channel: ComedyChannel }) {
  return <Badge tone={channel.status === 'ACTIVE' ? 'success' : channel.status === 'PAUSED' ? 'warning' : 'neutral'}>{channel.name} · {channel.language.toUpperCase()}</Badge>;
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {hint && <span className="field__hint">{hint}</span>}
    </label>
  );
}

export function ComedySection({ title, description, children, actions }: { title: ReactNode; description?: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <Card title={<div><div style={{ fontWeight: 700 }}>{title}</div>{description && <div className="muted" style={{ fontWeight: 400, fontSize: 12, marginTop: 2 }}>{description}</div>}</div>} actions={actions}>
      {children}
    </Card>
  );
}
